/**
 * Project Settings Page
 * 
 * Configure project-level settings including:
 * - Application Classes (target systems for AC generation)
 * - Domain Restrictions
 * - Knowledge Sources
 * - General Settings
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  ArrowLeft,
  Settings,
  Layers,
  Globe,
  Book,
  Sliders,
  ChevronRight,
  Loader2,
  AlertCircle,
  Plug,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import { ApplicationClassSelector } from '../../components/ApplicationClassSelector';
import { DomainSettings } from '../../components/DomainSettings';

// ============================================
// Types
// ============================================

interface ProjectDetail {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
  key: string;
  description: string | null;
  status: string;
}

type SettingsTab = 'application-classes' | 'domains' | 'knowledge' | 'integrations' | 'general';

// ============================================
// Tab Configuration
// ============================================

const TABS: { id: SettingsTab; label: string; icon: React.ReactNode; description: string }[] = [
  {
    id: 'application-classes',
    label: 'Target Systems',
    icon: <Layers className="w-5 h-5" />,
    description: 'Configure which systems receive generated AC',
  },
  {
    id: 'domains',
    label: 'Domain Restrictions',
    icon: <Globe className="w-5 h-5" />,
    description: 'Limit which URLs can be captured',
  },
  {
    id: 'knowledge',
    label: 'Knowledge Sources',
    icon: <Book className="w-5 h-5" />,
    description: 'Manage Confluence and document sources',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: <Plug className="w-5 h-5" />,
    description: 'Connect Cursor IDE and other tools',
  },
  {
    id: 'general',
    label: 'General Settings',
    icon: <Sliders className="w-5 h-5" />,
    description: 'Project name, key, and other options',
  },
];

// ============================================
// Component
// ============================================

export function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const setHeader = usePageHeaderStore((s) => s.setHeader);

  // Get current tab from URL hash or default to first tab
  const currentTab = (location.hash.slice(1) as SettingsTab) || 'application-classes';

  // Fetch project details
  const { data: project, isLoading, error } = useQuery<ProjectDetail>({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await api.get<{ data: ProjectDetail }>(`/projects/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
  });

  // Update page header
  useEffect(() => {
    if (project) {
      setHeader(
        'Project Settings',
        project.name,
        <Settings className="w-6 h-6" />
      );
    }
  }, [project, projectId, setHeader]);

  // Handle tab change
  const setTab = (tab: SettingsTab) => {
    navigate(`#${tab}`, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="p-6">
        <div className="p-4 bg-error-50 border border-error-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error-600" />
          <div>
            <p className="font-medium text-error-900">Failed to load project</p>
            <p className="text-sm text-error-700">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Back Link */}
      <Link
        to={`/projects/${projectId}`}
        className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {project.name}
      </Link>

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
          <Settings className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Project Settings</h1>
          <p className="text-neutral-600">{project.name} ({project.key})</p>
        </div>
      </div>

      {/* Layout: Sidebar + Content */}
      <div className="flex gap-8">
        {/* Sidebar Navigation */}
        <nav className="w-64 flex-shrink-0">
          <div className="space-y-1">
            {TABS.map((tab) => {
              const isActive = currentTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left
                    transition-colors duration-150
                    ${isActive 
                      ? 'bg-primary-50 text-primary border border-primary-200' 
                      : 'text-neutral-700 hover:bg-neutral-50'
                    }
                  `}
                >
                  <div className={isActive ? 'text-primary' : 'text-neutral-500'}>
                    {tab.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{tab.label}</div>
                    <div className="text-xs text-neutral-500 truncate">
                      {tab.description}
                    </div>
                  </div>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 text-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            {/* Application Classes Tab */}
            {currentTab === 'application-classes' && (
              <ApplicationClassSelector 
                projectId={projectId!}
                onSave={() => {
                  // Could show a toast notification here
                }}
              />
            )}

            {/* Domains Tab */}
            {currentTab === 'domains' && (
              <DomainSettings projectId={projectId!} initialDomains={[]} />
            )}

            {/* Knowledge Sources Tab */}
            {currentTab === 'knowledge' && (
              <div className="text-center py-12">
                <Book className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">
                  Knowledge Sources
                </h3>
                <p className="text-neutral-600 mb-4">
                  Configure Confluence spaces and document sources for RAG search.
                </p>
                <Link
                  to={`/projects/${projectId}/knowledge-sources`}
                  className="btn btn-primary"
                >
                  Manage Knowledge Sources
                </Link>
              </div>
            )}

            {/* Integrations Tab */}
            {currentTab === 'integrations' && (
              <CursorIntegrationPanel projectId={projectId!} />
            )}

            {/* General Settings Tab */}
            {currentTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                    General Settings
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Project Name */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Project Name
                      </label>
                      <input
                        type="text"
                        className="input w-full"
                        defaultValue={project.name}
                        disabled
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Contact an admin to change the project name.
                      </p>
                    </div>

                    {/* Project Key */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Project Key
                      </label>
                      <input
                        type="text"
                        className="input w-full font-mono"
                        defaultValue={project.key}
                        disabled
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Used in URLs and exports. Cannot be changed.
                      </p>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Description
                      </label>
                      <textarea
                        className="input w-full h-24 resize-none"
                        defaultValue={project.description || ''}
                        placeholder="Add a description for this project..."
                      />
                    </div>

                    {/* Organization */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Organization
                      </label>
                      <input
                        type="text"
                        className="input w-full"
                        defaultValue={project.orgName}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="pt-6 border-t border-neutral-200">
                  <h4 className="text-sm font-semibold text-error-600 mb-4">
                    Danger Zone
                  </h4>
                  <div className="p-4 border border-error-200 rounded-lg bg-error-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-error-900">Archive Project</p>
                        <p className="text-sm text-error-700">
                          Archived projects are hidden but can be restored.
                        </p>
                      </div>
                      <button className="btn bg-error-600 text-white hover:bg-error-700">
                        Archive
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Cursor Integration Panel
// ============================================

function CursorIntegrationPanel({ projectId }: { projectId: string }) {
  const [copied, setCopied] = useState(false);

  const generateConfig = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        config: any;
        configJson: string;
        deeplink: string;
        tokenId: string;
        tokenPrefix: string;
        instructions: string[];
        message: string;
      }>(`/api-keys/project/${projectId}/cursor-config`, {});
      return response;
    },
  });

  const handleCopy = async () => {
    if (!generateConfig.data?.configJson) return;
    await navigator.clipboard.writeText(generateConfig.data.configJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">
          Cursor IDE Integration
        </h3>
        <p className="text-neutral-600 text-sm">
          Connect Specuno to Cursor so your AI agents can access requirements,
          report implementations, and keep your knowledge base up to date.
        </p>
      </div>

      {!generateConfig.data ? (
        <div className="p-6 border border-neutral-200 rounded-xl bg-neutral-50 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <Plug className="w-8 h-8 text-primary" />
          </div>
          <h4 className="text-lg font-medium text-neutral-900 mb-2">
            Connect to Cursor
          </h4>
          <p className="text-sm text-neutral-600 mb-6 max-w-md mx-auto">
            Generate a configuration with a secure API token.
            Paste it into your <code className="text-xs bg-neutral-200 px-1.5 py-0.5 rounded">.cursor/mcp.json</code> file
            and Specuno tools will appear in Cursor Agent mode.
          </p>
          <button
            onClick={() => generateConfig.mutate()}
            disabled={generateConfig.isPending}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            {generateConfig.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plug className="w-4 h-4" />
            )}
            Generate Config
          </button>
          {generateConfig.error && (
            <p className="mt-3 text-sm text-error-600">
              {generateConfig.error instanceof Error
                ? generateConfig.error.message
                : 'Failed to generate config'}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Config JSON */}
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">
                MCP Configuration
              </label>
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-700 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy Config
                  </>
                )}
              </button>
            </div>
            <pre className="p-4 bg-neutral-900 text-neutral-100 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre">
              {generateConfig.data.configJson}
            </pre>
          </div>

          {/* Instructions */}
          <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
            <h4 className="font-medium text-primary-900 mb-2">Setup Instructions</h4>
            <ol className="text-sm text-primary-800 space-y-1 list-decimal list-inside">
              {generateConfig.data.instructions.map((step: string, i: number) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Deeplink */}
          <div className="flex items-center gap-3">
            <a
              href={generateConfig.data.deeplink}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Cursor
            </a>
            <button
              onClick={() => generateConfig.reset()}
              className="btn bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            >
              Generate New Token
            </button>
          </div>

          {/* Security note */}
          <p className="text-xs text-neutral-500">
            The token is shown only once. If you lose it, generate a new one.
            Token prefix: <code className="bg-neutral-100 px-1 rounded">{generateConfig.data.tokenPrefix}...</code>
          </p>
        </div>
      )}
    </div>
  );
}

export default ProjectSettingsPage;
